/**
 * Prediction Service
 *
 * Handles ML prediction requests with caching, retry functionality, and asynchronous polling.
 * This service helps manage long-running ML predictions more effectively.
 */

import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { apiLogger } from './apiLogger';

// In-memory cache for prediction results
const predictionCache = new Map<string, any>();

// In-memory store for task statuses
const taskStatusCache = new Map<string, TaskStatus>();

// Generate a cache key based on the request data
const generateCacheKey = (endpoint: string, data: any): string => {
  return `${endpoint}:${JSON.stringify(data)}`;
};

interface PredictionOptions {
  timeout?: number;
  useCache?: boolean;
  retries?: number;
  retryDelay?: number;
  pollInterval?: number; // Time in ms between status checks
  maxPollAttempts?: number; // Maximum number of polling attempts
}

/**
 * Task status response type
 */
export interface TaskStatus {
  task_id: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  created_at?: string;
  started_at?: string;
  completed_at?: string;
  result?: any;
  error?: string;
  status_url?: string;
}

/**
 * Traditional synchronous prediction request (will be deprecated)
 * Makes a prediction request with built-in caching, timeout management and retry logic
 */
export const makePrediction = async <T>(
  baseUrl: string,
  endpoint: string,
  data: any,
  token: string,
  options: PredictionOptions = {}
): Promise<T> => {
  const {
    timeout = 60000,
    useCache = true,
    retries = 2,
    retryDelay = 1000
  } = options;

  // Generate cache key and check for cached results
  const cacheKey = generateCacheKey(endpoint, data);
  
  if (useCache && predictionCache.has(cacheKey)) {
    apiLogger.debug('Using cached prediction result', { endpoint, cacheKey });
    return predictionCache.get(cacheKey);
  }

  // Helper function for making the actual request
  const makeRequest = async (attempt: number = 0): Promise<T> => {
    const url = `${baseUrl}/${endpoint}`;
    
    apiLogger.debug(`Making prediction request (attempt ${attempt + 1}/${retries + 1})`, {
      url,
      timeout,
      dataSize: JSON.stringify(data).length
    });
    
    try {
      const response = await axios.post<T>(
        url,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          timeout
        }
      );
      
      apiLogger.debug('Prediction request successful', { status: response.status });
      
      // Cache the result
      if (useCache) {
        predictionCache.set(cacheKey, response.data);
      }
      
      return response.data;
    } catch (error) {
      // If we have retries left, wait and try again
      if (attempt < retries) {
        apiLogger.debug('Retrying prediction request after error', { 
          error: axios.isAxiosError(error) ? error.message : 'Unknown error',
          nextAttempt: attempt + 1,
          delayMs: retryDelay
        });
        
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        return makeRequest(attempt + 1);
      }
      
      // No more retries, throw the error
      apiLogger.error('Prediction request failed after retries', error);
      throw error;
    }
  };
  
  return makeRequest();
};

/**
 * Starts an async prediction task and returns a task ID
 * This implements the asynchronous polling pattern
 */
export const startPredictionTask = async (
  baseUrl: string,
  endpoint: string,
  data: any,
  token: string,
  options: PredictionOptions = {}
): Promise<TaskStatus> => {
  const {
    timeout = 30000,
    retries = 2,
    retryDelay = 1000
  } = options;

  const url = `${baseUrl}/${endpoint}`;
  
  apiLogger.debug(`Starting async prediction task`, {
    url,
    timeout,
    dataSize: JSON.stringify(data).length
  });
  
  // Helper function for making the actual request
  const makeRequest = async (attempt: number = 0): Promise<TaskStatus> => {
    try {
      const response = await axios.post<TaskStatus>(
        url,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          timeout
        }
      );
      
      apiLogger.debug('Async prediction task started', { 
        status: response.status,
        taskId: response.data.task_id
      });
      
      // Store task status in cache
      taskStatusCache.set(response.data.task_id, response.data);
      
      return response.data;
    } catch (error) {
      // 422 = schema validation rejection. Surface the field-level detail
      // immediately and don't retry — the payload won't get any better.
      if (axios.isAxiosError(error) && error.response?.status === 422) {
        apiLogger.error('Async prediction task rejected (422 validation)', {
          url,
          responseData: error.response.data,
          requestPayloadKeys: Object.keys(data || {}),
        });
        // Print the FastAPI detail array as plain JSON so the field name
        // is visible without expanding nested console objects.
        console.error(
          '[predictionService] 422 validation error from',
          url,
          '\nDetail:',
          JSON.stringify(error.response.data, null, 2)
        );
        throw error;
      }

      // If we have retries left, wait and try again
      if (attempt < retries) {
        apiLogger.debug('Retrying async task creation after error', {
          error: axios.isAxiosError(error) ? error.message : 'Unknown error',
          nextAttempt: attempt + 1,
          delayMs: retryDelay
        });

        await new Promise(resolve => setTimeout(resolve, retryDelay));
        return makeRequest(attempt + 1);
      }

      // No more retries, throw the error
      apiLogger.error('Async prediction task failed to start', error);
      throw error;
    }
  };

  return makeRequest();
};

/**
 * Checks the status of an async prediction task
 */
export const checkTaskStatus = async (
  baseUrl: string,
  taskId: string,
  token: string,
  options: PredictionOptions = {}
): Promise<TaskStatus> => {
  const { timeout = 10000 } = options;
  
  const url = `${baseUrl}/analyze-station/status/${taskId}`;
  
  try {
    const response = await axios.get<TaskStatus>(
      url,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        timeout
      }
    );
    
    // Update task status in cache
    taskStatusCache.set(taskId, response.data);
    
    return response.data;
  } catch (error) {
    apiLogger.error('Failed to check task status', { taskId, error });
    throw error;
  }
};

/**
 * Polls a task until it completes or fails
 * Returns the final result or throws an error
 */
export const pollTaskUntilComplete = async <T>(
  baseUrl: string,
  taskId: string,
  token: string,
  options: PredictionOptions = {}
): Promise<T> => {
  const {
    pollInterval = 2000,
    maxPollAttempts = 30,
    useCache = true
  } = options;
  
  // Generate cache key
  const cacheKey = `task:${taskId}`;
  
  // Check if we already have the result cached
  if (useCache && predictionCache.has(cacheKey)) {
    apiLogger.debug('Using cached task result', { taskId, cacheKey });
    return predictionCache.get(cacheKey);
  }
  
  let attempts = 0;
  
  while (attempts < maxPollAttempts) {
    attempts++;
    
    const taskStatus = await checkTaskStatus(baseUrl, taskId, token, options);
    
    apiLogger.debug(`Polling task status (attempt ${attempts}/${maxPollAttempts})`, { 
      taskId, 
      status: taskStatus.status 
    });
    
    if (taskStatus.status === 'completed' && taskStatus.result) {
      // Cache the result
      if (useCache) {
        predictionCache.set(cacheKey, taskStatus.result);
      }
      return taskStatus.result as T;
    }
    
    if (taskStatus.status === 'failed') {
      throw new Error(`Task failed: ${taskStatus.error || 'Unknown error'}`);
    }
    
    // Wait before checking again
    await new Promise(resolve => setTimeout(resolve, pollInterval));
  }
  
  throw new Error(`Task polling timed out after ${maxPollAttempts} attempts`);
};

/**
 * Clears the prediction cache
 */
export const clearPredictionCache = (): void => {
  predictionCache.clear();
};

/**
 * Clears the task status cache
 */
export const clearTaskStatusCache = (): void => {
  taskStatusCache.clear();
};

/**
 * Check if a prediction is available in the cache
 */
export const hasCachedPrediction = (endpoint: string, data: any): boolean => {
  const cacheKey = generateCacheKey(endpoint, data);
  return predictionCache.has(cacheKey);
};

/**
 * Check if a task result is available in the cache
 */
export const hasCachedTaskResult = (taskId: string): boolean => {
  const cacheKey = `task:${taskId}`;
  return predictionCache.has(cacheKey);
};

/**
 * Get a task status from the cache
 */
export const getCachedTaskStatus = (taskId: string): TaskStatus | undefined => {
  return taskStatusCache.get(taskId);
};

/**
 * Get the size of the prediction cache
 */
export const getCacheSize = (): number => {
  return predictionCache.size;
};

export default {
  // Legacy synchronous API
  makePrediction,
  
  // New asynchronous polling API
  startPredictionTask,
  checkTaskStatus,
  pollTaskUntilComplete,
  
  // Cache management
  clearPredictionCache,
  clearTaskStatusCache,
  hasCachedPrediction,
  hasCachedTaskResult,
  getCachedTaskStatus,
  getCacheSize
};
